// ClubNotificationsPage Component
// Dedicated page for club notification template management

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import { useUser } from '../../context/UserContext';
import TemplateManagement from '../../components/Notifications/TemplateManagement';
import styles from './ClubNotificationsPage.module.css';

const ClubNotificationsPage = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const { userClubs } = useUser();
  
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canManage, setCanManage] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Check if user can manage this club
  useEffect(() => {
    if (userClubs && userClubs.length > 0 && clubId) {
      const membership = userClubs.find(m => {
        const membershipClubId = m.club?._id || m.club;
        return String(membershipClubId) === String(clubId);
      });
      if (membership && ['creator', 'owner', 'manager', 'admin'].includes(membership.role?.toLowerCase())) {
        setCanManage(true);
      }
    }
  }, [userClubs, clubId]);

  // Fetch club details
  useEffect(() => {
    const fetchClub = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${backendUrl}/api/clubs/${clubId}`);
        if (!res.ok) throw new Error('Club not found');
        const data = await res.json();
        setClub(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (clubId) {
      fetchClub();
    }
  }, [clubId, backendUrl]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <ClipLoader color="var(--primary)" size={50} />
          <p>Loading notification settings...</p>
        </div>
      </div>
    );
  }

  if (error || !club) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <h2>Error</h2>
          <p>{error || 'Club not found'}</p>
          <button className={styles.backBtn} onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <h2>Access Denied</h2>
          <p>You don't have permission to manage notifications for this club.</p>
          <button className={styles.backBtn} onClick={() => navigate(`/club/${clubId}`)}>
            Back to Club
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button 
          className={styles.backBtn}
          onClick={() => navigate(`/club/${clubId}`)}
        >
          <FaArrowLeft />
          <span>Back to {club.name}</span>
        </button>
      </div>

      {/* Template Management Component */}
      <div className={styles.content}>
        <TemplateManagement clubId={clubId} />
      </div>
    </div>
  );
};

export default ClubNotificationsPage;

